import {FC, useEffect, useState} from 'react';
import styled from '@emotion/styled';
import Box from '@mui/material/Box';
import {
  DataGrid,
  GridColDef,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridValueGetterParams
} from '@mui/x-data-grid';
import { FaEye, FaPencilAlt, FaTrash } from 'react-icons/fa';
import { IconButton } from '@mui/material';
// custom
import {IEvent} from '../../common/state/events/event';
import SimpleSpinner from '../loaders/spinners/SimpleSpinner';

type IEventAdminMuiTableProps = {
    list: IEvent[] | undefined,
    onView: (data : IEvent) => void,
    onEdit: (data : IEvent) => void,
    onDelete: (data : IEvent) => void
};


type IEventTableProps = Omit<IEvent, 'id'> & { 'id': number };

type IMuiTableProps = {
  list: IEventTableProps[];
  onView: (data : IEvent) => void,
  onEdit: (data : IEvent) => void,
  onDelete: (data : IEvent) => void
}

const ContainerStyled = styled.section`
    width: 100%;
    min-height: 300px;
    max-height: 600px;
    table {
        width: 100%
    }
`;

const CustomToolbar = () => {
  return (
    <GridToolbarContainer sx={{ justifyContent: 'flex-end', backgroundColor: '#f5ebe0', '& .MuiButton-root, & .MuiButtonBase-root': { color: 'black' } }}>
      <GridToolbarColumnsButton placeholder="" onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} onResize={undefined} onResizeCapture={undefined} />
      <GridToolbarFilterButton placeholder="" onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} onResize={undefined} onResizeCapture={undefined} />
      <GridToolbarExport />
    </GridToolbarContainer>
  );
};

// Mui Table component
const MuiTableAdmin: FC<IMuiTableProps> = ({ list, onView, onEdit, onDelete }) => {

  // material columns
  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 10,
      headerClassName: 'custom-header',
    },
    {
      field: 'title',
      headerName: 'Title',
      sortable: true,
      flex: 1,
      headerClassName: 'custom-header',
    },
    {
      field: 'description',
      headerName: 'Description',
      sortable: true,
      flex: 1,
      headerClassName: 'custom-header',
    },
    {
      field: 'start_date',
      headerName: 'Start Date',
      type: 'date',
      width: 200,
      sortable: true,
      headerClassName: 'custom-header',
      valueGetter: (data: GridValueGetterParams) => {
        return new Date(data.row.start_date);
      },
    },
    {
      field: 'end_date',
      type: 'date',
      headerName: 'End Date',
      sortable: true,
      width: 200,
      headerClassName: 'custom-header',
      valueGetter: (data: GridValueGetterParams) => {
        return new Date(data.row.end_date);
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      width: 200,
      headerClassName: 'custom-header',
      renderCell: (params) => {
        return (
          <div className="flex justify-around">
            <IconButton onClick={() => onView(params.row as IEvent)}>
              <FaEye />
            </IconButton>
            <IconButton onClick={() => onEdit(params.row as IEvent)}>
              <FaPencilAlt />
            </IconButton>
            <IconButton onClick={() => onDelete(params.row as IEvent)}>
              <FaTrash />
            </IconButton>
          </div>
        );
      }
    }
  ];

  return (<Box sx={{ height: 400, width: '100%' }}>
    <DataGrid
      style={{ fontSize: 15 }}
      rows={list}
      columns={columns}
      components={{ Toolbar: CustomToolbar }}
      sx={{
        '.custom-header': {
          backgroundColor: '#e3d5ca',
          fontWeight: 600,
          color: 'black',
        },
      }}
    />
  </Box>
  );
};


const EventAdminMuiTable : FC <IEventAdminMuiTableProps> = ({list, onView, onEdit, onDelete}) => {
    // states
    const [title, setTitle] = useState<string>('');
    const [subTitle, setSubTitle] = useState<string>('Please wait...');
    const [eventTableList, setEventTableList] = useState<IEventTableProps[]>([]);

    // lifecycle
    useEffect(()=> {
        if (list && list.length > 0) {
            const tableList: IEventTableProps[] = list.map((item: IEvent, idx: number) => ({ ...item, id: idx + 1 }));
            setEventTableList(tableList);
            setTitle('Event List Detail');
            setSubTitle('List of current and upcoming events...');
        } else if (list && list.length === 0) {
            setTitle('No Events');
            setSubTitle('There are currently no events. Click on + to add an event');
        }
    },[list]);

    return <ContainerStyled
        className="mt-2 relative bg-gray-100 dark:bg-gray-900 rounded-lg
                     flex flex-col justify-start items-start shadow-lg
                    p-4 overflow-hidden overflow-scroll">
        <div role="status"></div>
        <h2 className="text-gray-700 dark:text-teal-400 text-2xl font-medium mb-2">{title}</h2>
        <p className="text-gray-700 dark:text-gray-300 dartext-base mb-4">
            {subTitle}
        </p>
        <SimpleSpinner display={`inline-block`} loading={!list} title={'Loading events...'}/>
        {Boolean(list) && (list as IEvent[]).length > 0 && <MuiTableAdmin list={eventTableList} onView={onView} onEdit={onEdit} onDelete={onDelete} />}
    </ContainerStyled>;
};

export default EventAdminMuiTable;
